/**
 * Strict runtime parsing/formatting for the synthetic "Poslovni tokovi" tab.
 *
 * The API projection is already bounded, but the UI never trusts a network shape:
 * every field is checked before rendering, unsafe source links are dropped, and a
 * malformed row degrades to an explicit "unavailable" state instead of raw text.
 */

import type {
  BusinessCatalogue,
  BusinessCatalogueResponse,
  BusinessIntegrationState,
  BusinessProjectContext,
  BusinessProposalView,
  BusinessReceiptView,
  BusinessRoleView,
  BusinessRunView,
  BusinessStepView,
} from "../api/client";

const IMPLEMENTED = "implemented_synthetic";
const PLANNED = "planned_unavailable";
const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/;
const STEP_STATUSES = new Set([
  "pending", "running", "succeeded", "failed", "skipped", "cancelled",
  "killed", "needs_review", "rejected", "expired", "invalidated",
]);
const PROPOSAL_STATUSES = new Set([
  "proposed", "approved", "rejected", "expired", "invalidated", "executing",
  "executed", "failed", "needs_review",
]);

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown, maxLength = 4000): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength || EMAIL_RE.test(trimmed)) return null;
  return trimmed;
}

function requiredText(value: unknown, maxLength = 4000): string {
  return text(value, maxLength) ?? "";
}

function integer(value: unknown, minimum = 0, maximum = 1_000_000): number | null {
  return typeof value === "number" && Number.isInteger(value)
    && value >= minimum && value <= maximum
    ? value
    : null;
}

function bool(value: unknown): boolean {
  return value === true;
}

function stringList(value: unknown, maxItems = 40): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, maxItems)
    .map((row) => text(row, 500))
    .filter((row): row is string => row !== null);
}

/** Only HTTPS on a reserved `.invalid` host is a displayable source link. */
export function safeBusinessSourceUrl(value: unknown): string | null {
  const raw = text(value, 1000);
  if (!raw || !raw.startsWith("https://")) return null;
  if (/[\s<>"']/.test(raw) || raw.includes("@")) return null;
  const rest = raw.slice("https://".length);
  const host = rest.split("/", 1)[0].split(":", 1)[0].toLowerCase();
  if (!host || !host.endsWith(".invalid")) return null;
  if (host.startsWith(".") || host.includes("..")) return null;
  return raw;
}

export function businessRoleStatusLabel(status: string): string {
  return status === IMPLEMENTED ? "Sintetički implementirano" : "Planirano / nedostupno";
}

export function businessStepLabel(stepId: string): string {
  const labels: Record<string, string> = {
    context: "Kontekst i plan",
    crm_review: "CRM pregled",
    research: "Research",
    sales_next_action: "Prodajni sljedeći korak",
    review: "Kontrola kvalitete",
    propose_create: "Prijedlog zadatka",
    receipt: "Potvrda i sažetak",
  };
  return labels[stepId] ?? stepId;
}

export function businessJobStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    queued: "u redu",
    running: "izvršava se",
    awaiting_approval: "čeka ljudsko odobrenje",
    awaiting_budget_approval: "čeka budžet",
    succeeded: "uspješno",
    failed: "neuspješno",
    cancelled: "otkazano",
    killed: "zaustavljeno",
    needs_review: "za pregled",
  };
  return labels[status] ?? status;
}

function parseRole(value: unknown): BusinessRoleView | null {
  const row = record(value);
  if (!row) return null;
  const status = row.implementation_status;
  if (status !== IMPLEMENTED && status !== PLANNED) return null;
  const identity = record(row.runtime_identity);
  return {
    planning_role_id: requiredText(row.planning_role_id, 120),
    name: requiredText(row.name, 160),
    name_en: requiredText(row.name_en, 160),
    inputs: stringList(row.inputs),
    outputs: stringList(row.outputs),
    allowed_capabilities: stringList(row.allowed_capabilities),
    allowed_tools: stringList(row.allowed_tools),
    decision_owner: requiredText(row.decision_owner, 120),
    implementation_status: status,
    runtime_identity: identity ? {
      agent_key: requiredText(identity.agent_key, 120),
      agent_version: requiredText(identity.agent_version, 40),
      provider_kind: requiredText(identity.provider_kind, 80),
      paid_agent: bool(identity.paid_agent),
      zia_agent: bool(identity.zia_agent),
    } : null,
    availability_reason: text(row.availability_reason, 1000),
  };
}

function parseCatalogue(value: unknown): BusinessCatalogue | null {
  const row = record(value);
  if (!row) return null;
  const workflow = record(row.workflow);
  if (!workflow) return null;
  const roles = Array.isArray(row.roles)
    ? row.roles.map(parseRole).filter((role): role is BusinessRoleView => role !== null)
    : [];
  const runtime = Array.isArray(row.runtime_agents)
    ? row.runtime_agents.slice(0, 30).map((item) => {
      const agent = record(item);
      if (!agent) return null;
      return {
        agent_key: requiredText(agent.agent_key, 120),
        agent_version: requiredText(agent.agent_version, 40),
        planning_role_id: requiredText(agent.planning_role_id, 120),
        provider_kind: requiredText(agent.provider_kind, 80),
        paid_agent: bool(agent.paid_agent),
        zia_agent: bool(agent.zia_agent),
      };
    }).filter((agent): agent is NonNullable<typeof agent> => agent !== null)
    : [];
  const rules = record(row.global_rules);
  const globalRules: Record<string, boolean> = {};
  if (rules) {
    for (const key of Object.keys(rules)) {
      if (Object.prototype.hasOwnProperty.call(rules, key)) {
        globalRules[key] = bool(rules[key]);
      }
    }
  }
  return {
    workflow: {
      key: requiredText(workflow.key, 120),
      version: integer(workflow.version, 1, 1000) ?? 1,
      workflow_id: requiredText(workflow.workflow_id, 120),
      request_alias: requiredText(workflow.request_alias, 120),
    },
    roles,
    runtime_agents: runtime,
    global_rules: globalRules,
  };
}

function parseContext(value: unknown): BusinessProjectContext | null {
  const row = record(value);
  if (!row) return null;
  return {
    schema_version: requiredText(row.schema_version, 120),
    project_id: requiredText(row.project_id, 80),
    program: requiredText(row.program, 80),
    version: integer(row.version, 1, 1000) ?? 1,
    content_hash: requiredText(row.content_hash, 120),
    product_summary: requiredText(row.product_summary, 2000),
    ideal_customer: requiredText(row.ideal_customer, 2000),
    allowed_claims: stringList(row.allowed_claims),
    disallowed_claims: stringList(row.disallowed_claims),
    approved_price_context: null,
    sales_stages: stringList(row.sales_stages),
    qualification: stringList(row.qualification),
    decision_owner_role: requiredText(row.decision_owner_role, 120),
    system_of_record: requiredText(row.system_of_record, 80),
    access_note: requiredText(row.access_note, 2000),
  };
}

function parseIntegration(value: unknown): BusinessIntegrationState | null {
  const row = record(value);
  if (!row) return null;
  const crm = record(row.crm);
  const zia = record(row.zia);
  if (!crm || !zia) return null;
  const cost = record(crm.cost);
  return {
    schema: requiredText(row.schema, 120),
    workflow_key: requiredText(row.workflow_key, 120),
    project_id: requiredText(row.project_id, 80),
    crm: {
      provider: requiredText(crm.provider, 80),
      live_connection: requiredText(crm.live_connection, 80),
      verified: bool(crm.verified),
      synthetic_only: bool(crm.synthetic_only),
      account_ref: null,
      data_centre: null,
      scopes: stringList(crm.scopes),
      cost: {
        status: requiredText(cost?.status, 80) || "unknown",
        amount_micros: integer(cost?.amount_micros, 0, 1_000_000_000) ?? null,
      },
      object_mapping: Array.isArray(crm.object_mapping)
        ? crm.object_mapping.slice(0, 20).map((item) => {
          const mapping = record(item);
          if (!mapping) return null;
          return {
            object: requiredText(mapping.object, 80),
            business_purpose: requiredText(mapping.business_purpose, 500),
            status: requiredText(mapping.status, 80),
            verified_field_ids: null,
          };
        }).filter((item): item is NonNullable<typeof item> => item !== null)
        : [],
      probe_checklist: stringList(crm.probe_checklist),
      controlled_write_checklist: stringList(crm.controlled_write_checklist),
    },
    zia: {
      availability: requiredText(zia.availability, 80),
      org_ref: null,
      agent_id: null,
      agent_version: null,
      tools: [],
      side_effect_class: requiredText(zia.side_effect_class, 80),
      connection_verified: bool(zia.connection_verified),
      cost_status: requiredText(zia.cost_status, 80),
      read_only_invocable: false,
      reason: requiredText(zia.reason, 1000),
    },
    live_calls_made: integer(row.live_calls_made, 0, 1000) ?? 0,
    credentials_read: bool(row.credentials_read),
    private_rows_read: bool(row.private_rows_read),
  };
}

export function parseBusinessCatalogueResponse(value: unknown): BusinessCatalogueResponse | null {
  const row = record(value);
  if (!row) return null;
  const catalogue = parseCatalogue(row.catalogue);
  const context = parseContext(row.project_context);
  const integration = parseIntegration(row.integration);
  const workflow = record(row.workflow);
  if (!catalogue || !context || !integration || !workflow) return null;
  if (context.project_id !== integration.project_id) return null;
  const steps = Array.isArray(workflow.steps)
    ? workflow.steps.slice(0, 20).map((item, index) => {
      const step = record(item);
      if (!step) return null;
      return {
        index: integer(step.index, 0, 100) ?? index,
        step_id: requiredText(step.step_id, 120),
        kind: requiredText(step.kind, 80),
        agent_key: requiredText(step.agent_key, 120),
        agent_version: requiredText(step.agent_version, 40),
        idempotent: bool(step.idempotent),
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null)
    : [];
  return {
    catalogue,
    project_context: context,
    integration,
    workflow: {
      key: requiredText(workflow.key, 120),
      version: integer(workflow.version, 1, 1000) ?? 1,
      workflow_id: requiredText(workflow.workflow_id, 120),
      content_hash: requiredText(workflow.content_hash, 120),
      request_alias: requiredText(workflow.request_alias, 120),
      steps,
    },
    synthetic_only: bool(row.synthetic_only),
    live_connection_verified: bool(row.live_connection_verified),
  };
}

function parseStep(value: unknown): BusinessStepView | null {
  const row = record(value);
  if (!row) return null;
  const status = text(row.status, 40);
  const stepId = requiredText(row.step_id, 120);
  if (!status || !STEP_STATUSES.has(status) || !stepId) return null;
  const summary = record(row.summary);
  const sourceVsInference = record(row.source_vs_inference);
  const safeSummary: Record<string, unknown> = {};
  if (summary) {
    for (const [key, item] of Object.entries(summary)) {
      if (typeof item === "string") {
        const safe = text(item, 2000);
        if (safe) safeSummary[key] = safe;
      } else if (typeof item === "number" || typeof item === "boolean") {
        safeSummary[key] = item;
      } else if (Array.isArray(item)) {
        safeSummary[key] = item.slice(0, 20).map((entry) => {
          if (typeof entry === "string") return text(entry, 500);
          const nested = record(entry);
          if (!nested) return null;
          const cleaned: Record<string, unknown> = {};
          let unsafeSource = false;
          for (const [nestedKey, nestedValue] of Object.entries(nested)) {
            if (typeof nestedValue === "string") {
              if (nestedKey === "url") {
                const safe = safeBusinessSourceUrl(nestedValue);
                if (!safe) unsafeSource = true;
                else cleaned[nestedKey] = safe;
              } else {
                const safe = text(nestedValue, 500);
                if (safe) cleaned[nestedKey] = safe;
              }
            } else if (typeof nestedValue === "number" || typeof nestedValue === "boolean") {
              cleaned[nestedKey] = nestedValue;
            }
          }
          return unsafeSource ? null : cleaned;
        }).filter((entry) => entry !== null);
      }
    }
  }
  return {
    index: integer(row.index, 0, 100) ?? 0,
    step_id: stepId,
    agent_key: text(row.agent_key, 120),
    agent_version: text(row.agent_version, 40),
    role: text(row.role, 120),
    kind: text(row.kind, 80),
    status,
    output_hash: text(row.output_hash, 120),
    dependency_reason: text(row.dependency_reason, 1000),
    source_vs_inference: sourceVsInference ? {
      source: stringList(sourceVsInference.source),
      inference: stringList(sourceVsInference.inference),
    } : null,
    summary: safeSummary,
  };
}

function parseProposal(value: unknown): BusinessProposalView | null {
  const row = record(value);
  if (!row) return null;
  const status = text(row.status, 40);
  const proposalId = requiredText(row.id, 120);
  const contentHash = requiredText(row.content_hash, 120);
  if (!status || !PROPOSAL_STATUSES.has(status) || !proposalId || !contentHash) {
    return null;
  }
  const target = record(row.target_ref);
  const after = record(row.after);
  const safeAfter = after && !EMAIL_RE.test(JSON.stringify(after)) ? after : null;
  return {
    id: proposalId,
    status,
    action_type: requiredText(row.action_type, 120),
    risk: requiredText(row.risk, 40),
    content_hash: contentHash,
    source_snapshot_hash: requiredText(row.source_snapshot_hash, 120),
    target_ref: {
      adapter: requiredText(target?.adapter, 80),
      connection_id: requiredText(target?.connection_id, 120),
      resource: requiredText(target?.resource, 120),
      external_id: null,
    },
    before: {},
    after: safeAfter,
    required_approvals: integer(row.required_approvals, 1, 2) ?? 1,
    decisions: Array.isArray(row.decisions)
      ? row.decisions.slice(0, 4).map((item) => {
        const decision = record(item);
        if (!decision) return null;
        return {
          user_id: requiredText(decision.user_id, 120),
          decision: requiredText(decision.decision, 40),
          decided_at: text(decision.decided_at, 80),
        };
      }).filter((item): item is NonNullable<typeof item> => item !== null)
      : [],
    invalidated_reason: text(row.invalidated_reason, 120),
    expires_at: text(row.expires_at, 80),
  };
}

function parseReceipt(value: unknown): BusinessReceiptView | null {
  const row = record(value);
  if (!row) return null;
  const cost = record(row.cost);
  const next = record(row.human_next_step);
  const summary = record(row.business_summary);
  const readback = record(row.readback);
  const after = record(row.after);
  return {
    status: requiredText(row.status, 40),
    effect: text(row.effect, 40),
    external_id: text(row.external_id, 120),
    approval_id: text(row.approval_id, 120) ?? undefined,
    proposal_content_hash: text(row.proposal_content_hash, 120) ?? undefined,
    target_resource: text(row.target_resource, 120) ?? undefined,
    before: {},
    after: after && !EMAIL_RE.test(JSON.stringify(after)) ? after : null,
    readback: readback ? {
      external_id: text(readback.external_id, 120),
      action_type: text(readback.action_type, 120),
      resource: text(readback.resource, 120),
      synthetic: bool(readback.synthetic),
      project_matches: bool(readback.project_matches),
    } : null,
    cost: cost ? {
      status: text(cost.status, 40) ?? undefined,
      amount_micros: integer(cost.amount_micros, 0, 1_000_000_000) ?? null,
      cost_scope: text(cost.cost_scope, 80) ?? undefined,
      live_provider_cost: text(cost.live_provider_cost, 80) ?? undefined,
      zia_cost: text(cost.zia_cost, 80) ?? undefined,
      currency: text(cost.currency, 20) ?? undefined,
    } : null,
    human_next_step: next ? {
      title: text(next.title, 200) ?? undefined,
      owner_role: text(next.owner_role, 80) ?? undefined,
      due_in_days: integer(next.due_in_days, 0, 365) ?? undefined,
      instruction: text(next.instruction, 1000) ?? undefined,
      human_owned: bool(next.human_owned),
    } : null,
    business_summary: summary ? {
      accepted: stringList(summary.accepted),
      not_accepted: stringList(summary.not_accepted),
      purchase_intent_claimed: bool(summary.purchase_intent_claimed),
    } : null,
    receipt_hash: text(row.receipt_hash, 120) ?? undefined,
  };
}

export function parseBusinessRunView(value: unknown): BusinessRunView | null {
  const row = record(value);
  if (!row) return null;
  const available = bool(row.available);
  const jobId = requiredText(row.job_id, 120);
  if (!jobId.startsWith("job_")) return null;
  if (!available) {
    return {
      schema: requiredText(row.schema, 120),
      available: false,
      reason: text(row.reason, 120) ?? "unavailable",
      job_id: jobId,
    };
  }
  const projectId = requiredText(row.project_id, 80);
  const context = parseContext(row.context);
  const integration = parseIntegration(row.integration);
  if (!projectId) return null;
  if (context && context.project_id !== projectId) return null;
  if (integration && integration.project_id !== projectId) return null;
  const steps = Array.isArray(row.steps)
    ? row.steps.map(parseStep).filter((step): step is BusinessStepView => step !== null)
    : [];
  const proposal = parseProposal(row.proposal);
  const receipt = parseReceipt(row.receipt);
  const next = record(row.next_human_task);
  const summary = record(row.business_summary);
  const states = record(row.states);
  return {
    schema: requiredText(row.schema, 120),
    available: true,
    job_id: jobId,
    project_id: projectId,
    workflow: record(row.workflow) ?? {},
    goal: text(row.goal, 500) ?? undefined,
    scenario_id: text(row.scenario_id, 80) ?? undefined,
    context: context ?? undefined,
    integration: integration ?? undefined,
    steps,
    proposal,
    receipt,
    next_human_task: next ? {
      title: text(next.title, 200) ?? undefined,
      owner_role: text(next.owner_role, 80) ?? undefined,
      due_in_days: integer(next.due_in_days, 0, 365) ?? undefined,
      instruction: text(next.instruction, 1000) ?? undefined,
      human_owned: bool(next.human_owned),
    } : null,
    business_summary: summary ? {
      accepted: stringList(summary.accepted),
      not_accepted: stringList(summary.not_accepted),
      purchase_intent_claimed: bool(summary.purchase_intent_claimed),
    } : null,
    states: states
      ? Object.fromEntries(Object.entries(states).map(([key, item]) => [key, bool(item)]))
      : undefined,
    synthetic_only: bool(row.synthetic_only),
    live_connection_verified: false,
  };
}
